import { useEffect, useRef, useState } from 'react'

import { buscarDireccion, esPuntoFino, puntoDeFiar } from '@/lib/geocodificacion/google'
import { cargarGoogleMaps, hayLlaveDeGoogle, ESTILO_MAPA } from '@/lib/mapas/google'
import { useGuardarUbicacionManual } from '@/modules/mapa-calor/useUbicarOrdenes'
import { Aviso } from '@/shared/ui/Aviso/Aviso'
import { Boton } from '@/shared/ui/Boton/Boton'

import estilos from './EditarUbicacion.module.css'

/**
 * PONER EL PUNTO DE LA ORDEN A MANO.
 *
 * El geocodificador acierta con una dirección urbana; con las de finca y sitio
 * con nombre —o las que importamos con la coordenada movida— no. Aquí quien
 * conoce Cartago arrastra el alfiler al lugar exacto y lo guarda. Se marca como
 * 'manual', que es la mejor fuente que hay, y el geocodificador ya no lo pisa.
 *
 * Va debajo de la dirección, en el mismo documento, para no mandar a nadie a
 * otra pantalla a corregir lo que está mirando.
 */

/** Cartago, plaza de Bolívar: el centro razonable cuando la orden no tiene punto. */
const CARTAGO = { lat: 4.746, lng: -75.9115 }

/** Los municipios donde trabaja MEGA, a un toque. Se puede escribir otro. */
const MUNICIPIOS = [
  'Cartago',
  'Pereira',
  'Dosquebradas',
  'Santa Rosa de Cabal',
  'La Virginia',
  'Armenia',
  'Obando',
  'Alcalá',
  'Ansermanuevo',
  'Toro',
  'La Unión',
  'Roldanillo',
  'Zarzal',
]

type Props = {
  ordenId: string
  direccion: string
  ciudad: string | null
  departamento: string | null
  latitud: number | null
  longitud: number | null
  origen: string | null
}

export function EditarUbicacion({
  ordenId,
  direccion,
  ciudad,
  departamento,
  latitud,
  longitud,
  origen,
}: Props) {
  const [abierto, setAbierto] = useState(false)
  const [punto, setPunto] = useState<{ lat: number; lng: number } | null>(null)
  const [buscando, setBuscando] = useState(false)
  const [avisoBusqueda, setAvisoBusqueda] = useState<string | null>(null)
  // Sin municipio Google no sabe dónde buscar: «CR 9 22 52» sola devolvió
  // «Colombia», y con Cartago por defecto otra dirección; con Pereira, la
  // exacta (OS-2026-0102). Arranca con el de la orden y se puede cambiar.
  const [municipio, setMunicipio] = useState(ciudad ?? 'Cartago')
  const [encontrada, setEncontrada] = useState<string | null>(null)
  const [listo, setListo] = useState(false)

  const contenedor = useRef<HTMLDivElement | null>(null)
  const mapa = useRef<google.maps.Map | null>(null)
  const alfiler = useRef<google.maps.Marker | null>(null)

  const { guardar, guardando, error } = useGuardarUbicacionManual()

  const tienePunto = latitud !== null && longitud !== null

  // Monta el mapa al abrir. El marcador se arrastra o se pone con un clic; en
  // ambos casos el punto guardado es el que quede debajo del alfiler.
  useEffect(() => {
    if (!abierto || !hayLlaveDeGoogle() || !contenedor.current) return

    let vivo = true
    const inicio = tienePunto ? { lat: Number(latitud), lng: Number(longitud) } : CARTAGO

    void cargarGoogleMaps().then(({ maps }) => {
      if (!vivo || !contenedor.current) return

      const m = new maps.Map(contenedor.current, {
        styles: ESTILO_MAPA,
        center: inicio,
        zoom: tienePunto ? 17 : 14,
        mapTypeControl: false,
        streetViewControl: false,
        gestureHandling: 'greedy',
      })

      const pin = new google.maps.Marker({
        map: m,
        position: inicio,
        draggable: true,
        title: 'Arrástralo al sitio exacto',
      })

      const fijar = (pos: google.maps.LatLng | null | undefined) => {
        if (!pos) return
        setPunto({ lat: pos.lat(), lng: pos.lng() })
      }

      pin.addListener('dragend', () => fijar(pin.getPosition()))
      m.addListener('click', (e: google.maps.MapMouseEvent) => {
        pin.setPosition(e.latLng)
        fijar(e.latLng)
      })

      mapa.current = m
      alfiler.current = pin
      setPunto(tienePunto ? inicio : null)
      setListo(true)
    })

    return () => {
      vivo = false
      mapa.current = null
      alfiler.current = null
      setListo(false)
    }
    // Solo se remonta al abrir/cerrar: mientras está abierto, el punto lo mueve
    // el usuario en el mapa, no un cambio de props.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abierto])

  // Busca la dirección escrita y lleva el alfiler ahí, como punto de partida
  // para afinarlo a mano. Útil en las importadas que llegaron sin coordenada.
  async function buscarPorLaDireccion() {
    setBuscando(true)
    setAvisoBusqueda(null)
    setEncontrada(null)
    const donde = municipio.trim()
    try {
      const encontrado = await buscarDireccion({
        direccion,
        ciudad: donde,
        // El departamento de la orden solo sirve si es el mismo municipio:
        // «Pereira, Valle del Cauca» confunde a Google.
        departamento: donde === (ciudad ?? '') ? departamento ?? '' : '',
      })
      if (!encontrado) {
        setAvisoBusqueda(`No se encontró esa dirección en ${donde}. Ubícala a mano en el mapa.`)
        return
      }
      // Cuando no la encuentra, Google no dice «no sé»: devuelve el centro del
      // municipio o del país. Eso no se pone como punto.
      if (!esPuntoFino(encontrado.precision)) {
        setAvisoBusqueda(
          `Google no encontró la dirección en ${donde}: solo devolvió «${encontrado.texto}». Revisa el municipio o ubícala a mano en el mapa.`,
        )
        return
      }
      // Y que sea la dirección pedida: con Cartago, «CR 9 22 52» volvía como
      // «Cra. 9 # 3-22» con precisión de casa. Google corrige, no avisa.
      const veredicto = puntoDeFiar(encontrado, { direccion, ciudad: donde, departamento: '' })
      if (!veredicto.sirve) {
        setAvisoBusqueda(`${veredicto.motivo} Revisa el municipio o ubícala a mano en el mapa.`)
        return
      }
      setEncontrada(encontrado.texto)
      const pos = { lat: encontrado.latitud, lng: encontrado.longitud }
      alfiler.current?.setPosition(pos)
      mapa.current?.panTo(pos)
      mapa.current?.setZoom(17)
      setPunto(pos)
    } catch {
      setAvisoBusqueda('No se pudo buscar la dirección. Ubícala a mano en el mapa.')
    } finally {
      setBuscando(false)
    }
  }

  async function alGuardar() {
    if (!punto) return
    const ok = await guardar(ordenId, punto.lat, punto.lng)
    if (ok) setAbierto(false)
  }

  if (!hayLlaveDeGoogle()) return null

  if (!abierto) {
    /* UN BOTÓN DE VERDAD. Johan: «este botón está súper escondido». Era un
       texto morado de 11 px pegado bajo la dirección y nadie lo veía. Y sin
       punto la llegada no se controla con geocerca —se acepta donde sea y la
       primera llegada precisa queda como punto—, así que en ese caso va como
       botón principal: es lo que falta. */
    return (
      <div className={`${estilos.cerrado} no-imprimir`}>
        <Boton
          variante={tienePunto ? 'secundario' : 'principal'}
          tamano="pequeno"
          onClick={() => setAbierto(true)}
        >
          {tienePunto ? 'Ajustar ubicación en el mapa' : 'Poner ubicación en el mapa'}
        </Boton>
        <span className={estilos.origen}>
          {!tienePunto
            ? 'sin ubicación: la llegada no se controla con geocerca'
            : origen === 'manual'
            ? 'puesta a mano'
            : origen === 'geocodificada'
            ? 'adivinada por la dirección'
            : origen
            ? 'punto de la zona'
            : ''}
        </span>
      </div>
    )
  }

  return (
    <div className={estilos.editor}>
      <p className={estilos.ayuda}>
        Arrastra el alfiler —o toca el mapa— hasta el sitio exacto y guarda. Quedará como ubicación
        puesta a mano.
      </p>

      <div ref={contenedor} className={estilos.mapa} />

      <div className={estilos.busqueda}>
        <label className={estilos.rotulo} htmlFor={`municipio-${ordenId}`}>
          Municipio
        </label>
        <input
          id={`municipio-${ordenId}`}
          className={estilos.municipio}
          list="municipios-mega"
          value={municipio}
          onChange={(e) => setMunicipio(e.target.value)}
          onFocus={(e) => e.currentTarget.select()}
        />
        <datalist id="municipios-mega">
          {MUNICIPIOS.map((m) => (
            <option key={m} value={m} />
          ))}
        </datalist>
        <Boton
          variante="secundario"
          tamano="pequeno"
          onClick={() => void buscarPorLaDireccion()}
          trabajando={buscando}
          disabled={municipio.trim().length === 0}
        >
          Buscar la dirección
        </Boton>
        {encontrada && (
          <span className={estilos.encontrada}>
            Google la ubicó en <b>{encontrada}</b>. Revisa el alfiler antes de guardar.
          </span>
        )}
      </div>

      {avisoBusqueda && (
        <div className={estilos.aviso}>
          <Aviso tipo="alerta">{avisoBusqueda}</Aviso>
        </div>
      )}
      {error && (
        <div className={estilos.aviso}>
          <Aviso tipo="error">{error}</Aviso>
        </div>
      )}

      <div className={estilos.pie}>
        <span className={estilos.coords}>
          {punto
            ? `${punto.lat.toFixed(6)}, ${punto.lng.toFixed(6)}`
            : 'Toca el mapa para poner el punto'}
        </span>

        <div className={estilos.botones}>
          <Boton variante="secundario" onClick={() => setAbierto(false)}>
            Cancelar
          </Boton>
          <Boton
            variante="principal"
            onClick={() => void alGuardar()}
            trabajando={guardando}
            disabled={!punto || !listo}
          >
            Guardar aquí
          </Boton>
        </div>
      </div>
    </div>
  )
}
